import { trpc } from '../../../../lib/trpc.js'

export interface CompanyTag {
  id: string
  name: string
  slug: string
  description: string | null
  order: number
  createdAt: string
  updatedAt: string
}

export function useCompanyTags() {
  const utils = trpc.useUtils()

  const {
    data: tags = [],
    isLoading: loading,
    error: queryError,
  } = trpc.admin.companyTags.list.useQuery()

  const error = queryError?.message ?? null

  const createMutation = trpc.admin.companyTags.create.useMutation({
    onSuccess: () => {
      utils.admin.companyTags.list.invalidate()
    },
  })

  const updateMutation = trpc.admin.companyTags.update.useMutation({
    onSuccess: () => {
      utils.admin.companyTags.list.invalidate()
    },
  })

  const deleteMutation = trpc.admin.companyTags.delete.useMutation({
    onSuccess: () => {
      utils.admin.companyTags.list.invalidate()
    },
  })

  const addTag = async (data: {
    name: string
    slug?: string
    description?: string
    order?: number
  }): Promise<CompanyTag> => {
    return createMutation.mutateAsync(data)
  }

  const updateTag = async (
    id: string,
    data: {
      name?: string
      slug?: string
      description?: string | null
      order?: number
    },
  ): Promise<CompanyTag> => {
    return updateMutation.mutateAsync({ id, ...data })
  }

  const reorderTags = async (orderedIds: string[]): Promise<void> => {
    // Optimistically update local cache
    utils.admin.companyTags.list.setData(undefined, (prev) => {
      if (!prev) return prev
      const tagMap = new Map(prev.map((t) => [t.id, t]))
      return orderedIds
        .map((id, index) => {
          const tag = tagMap.get(id)
          return tag ? { ...tag, order: index } : null
        })
        .filter((t): t is CompanyTag => t !== null)
    })

    // Persist each tag's new order
    await Promise.all(
      orderedIds.map((id, index) =>
        updateMutation.mutateAsync({ id, order: index }),
      ),
    )
  }

  const deleteTag = async (id: string): Promise<void> => {
    await deleteMutation.mutateAsync({ id })
  }

  const refresh = () => {
    utils.admin.companyTags.list.invalidate()
  }

  return {
    tags,
    loading,
    error,
    refresh,
    addTag,
    updateTag,
    deleteTag,
    reorderTags,
  }
}
